import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { useNavigate, useLocation } from 'react-router';
import {
  APPROVE_OR_REJECT_MEDIA_APPROVAL,
  ApproveOrRejectMediaApprovalData,
  ApproveOrRejectMediaApprovalVariables,
} from '@jwp/ott-common/src/graphql/mutation/moderate.mutation';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';

import Button from '../Button/Button';
import TextField from '../TextField/TextField';

import styles from './RejectReasonModal.module.scss';

const RejectReasonModal: React.FC = () => {
  const { t } = useTranslation('upload');
  const navigate = useNavigate();
  const location = useLocation();
  const maId = useQueryParam('id');
  const message = useQueryParam('message');
  const [reason, setReason] = useState('');

  const [approveOrReject, { loading: mutationLoading }] = useMutation<ApproveOrRejectMediaApprovalData, ApproveOrRejectMediaApprovalVariables>(
    APPROVE_OR_REJECT_MEDIA_APPROVAL,
    {
      onCompleted: () => {
        navigate(createURLFromLocation(location, { 
          u: message === 'video' ? 'rejection-success-video' : 'rejection-success', 
          id: null 
        }));
      },
      refetchQueries: ['getAllMediaApprovalsList'],
    },
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!maId || !reason.trim()) return;

    approveOrReject({
      variables: {
        input: {
          maId,
          maStatus: 'REJECTED',
          maRejectionReason: reason.trim(),
        },
      },
    });
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('rejection.title')}</h2>
      <form onSubmit={handleSubmit} className={styles.form}>
        <TextField
          name="description"
          label={t('rejection.form.description_label')}
          placeholder={t('rejection.form.description_placeholder')}
          className={styles.reasonField}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          rows={10}
          required
          disabled={mutationLoading}
        />
        <div className={styles.actions}>
          <Button
            type="submit"
            label={t('rejection.form.submit_button')}
            color="primary"
            variant="contained"
            fullWidth
            disabled={!reason.trim() || mutationLoading}
            busy={mutationLoading}
          />
        </div>
      </form>
    </div>
  );
};

export default RejectReasonModal;
